import React, { useEffect } from 'react';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import SignIn from '../screens/SignIn';
import AppSwitcher from '../screens/AppSwitcher/AppSwitcher';
import TroubleLoggingIn from '../screens/TroubleLoggingIn/trouble-logging-in';
import CreateAccount from '../screens/CreateAccount/create-account';
import ResetPasswordOrActivateAccount from '../screens/ResetPassword';
import AuthenticatedRouteWrapper from './authenticated-route';
import { oktaAuthJSClient, getOauthBrowsingContextOrigin, handOffEhrOauthPopupToOpener } from '../okta';
import {
  needsAuth0SessionInit,
  startAuth0SessionInit,
  AUTH0_SESSION_INIT_REDIRECTING,
} from '../okta/auth0-session-init';
import { Security, LoginCallback } from '@okta/okta-react';
import EhrOauthHandoffListener from './ehr-oauth-handoff-listener';
import logger from '../utils/logger';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { createBrowserHistory } from 'history';
import EhrLaunch from '../screens/EhrLaunch';
import EhrCallback from '../screens/EhrCallback';
import Subscriptions from '../screens/Subscriptions';
import Auth0SessionCallback from '../screens/Auth0SessionCallback';
import { ROUTES } from './routes';
import Loader from '@tcl-boron-prefabs/loader';
import cn from 'classnames';
import withStyles from '@tempus/component-library/utils/withStyles';
import styles from '../styles';

export { ROUTES };

const history = createBrowserHistory();

function callbackLoading(classes) {
  return (
    <div className={cn('app-view-container', classes.loadingContainer)} data-testid="okta-login-callback-loading">
      <Loader />
    </div>
  );
}

/** prompt=none recover failed: keep the loader up and return to fromLogin. */
function OktaCallbackError({ error, classes }) {
  const code = error?.errorCode || error?.error;
  const silent = code === 'login_required' || code === 'interaction_required';

  useEffect(() => {
    if (silent) {
      history.replace('/?fromLogin=true&oktaRecovered=1');
    }
  }, [silent]);

  if (silent) {
    return callbackLoading(classes);
  }

  return (
    <div className="app-view-container" data-testid="okta-login-callback-error">
      <p>{error?.message || 'Sign-in failed'}</p>
    </div>
  );
}

const StyledOktaCallbackError = withStyles(styles)(OktaCallbackError);

function OktaLoginCallback({ classes }) {
  return <LoginCallback errorComponent={StyledOktaCallbackError} loadingElement={callbackLoading(classes)} />;
}

const StyledOktaLoginCallback = withStyles(styles)(OktaLoginCallback);

function AppRouter() {
  function authHandler() {
    history.push(ROUTES.ROOT);
  }

  async function restoreOriginalUri(oktaAuth, originalUri) {
    if (await handOffEhrOauthPopupToOpener(oktaAuth, originalUri)) {
      return;
    }
    if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
      logger.warn('EHR OAuth popup handoff failed; completing login in popup window');
    }
    // Initialize Auth0 session for internal users.
    try {
      const tokens = await oktaAuth.tokenManager.getTokens();
      const email = tokens?.idToken?.claims?.email;
      if (needsAuth0SessionInit(email) && (await startAuth0SessionInit(email)) === AUTH0_SESSION_INIT_REDIRECTING) {
        return;
      }
    } catch (err) {
      logger.warn('Failed to evaluate Auth0 session init eligibility during login callback', { err });
    }
    history.push(
      toRelativeUrl(originalUri || '/', getOauthBrowsingContextOrigin(), {
        replace: true,
      }),
    );
  }

  return (
    <Router history={history}>
      <Security oktaAuth={oktaAuthJSClient} onAuthRequired={authHandler} restoreOriginalUri={restoreOriginalUri}>
        <EhrOauthHandoffListener />
        <Switch>
          <Route path={ROUTES.AUTH0_SESSION_CALLBACK} exact component={Auth0SessionCallback} />
          <Route path={ROUTES.ROOT} exact component={SignIn} />
          <Route path={ROUTES.CREATE_ACCOUNT} exact component={CreateAccount} />
          <Route path={ROUTES.SUBSCRIPTIONS} exact component={Subscriptions} />
          <Route path={ROUTES.TROUBLE_LOGGING_IN} exact component={TroubleLoggingIn} />
          <Route path={ROUTES.EHR_OKTA_CALLBACK} component={StyledOktaLoginCallback} />
          <Route path={ROUTES.IMPLICIT_CALLBACK} component={StyledOktaLoginCallback} />
          <Route path={ROUTES.RESET_PASSWORD_OR_ACTIVATE_ACCOUNT} component={ResetPasswordOrActivateAccount} />
          <Route path={ROUTES.EHR_LAUNCH} component={EhrLaunch} />
          <Route path={ROUTES.EHR_CALLBACK} component={EhrCallback} />
          <AuthenticatedRouteWrapper path={ROUTES.APP_SWITCHER} component={AppSwitcher} />
          <Redirect path={ROUTES.SIGNIN} to={ROUTES.ROOT} />
        </Switch>
      </Security>
    </Router>
  );
}

export default AppRouter;
